import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TanTable } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import api from '../../services/api';
import { MapIcon } from '@heroicons/react/24/outline';
import { GeofenceDrawer } from '../../components/GeofenceDrawer';
import { LocationEditDrawer } from '../../components/LocationEditDrawer';
import { DupeLocation } from '../../services/locations.service';
import { deletedRowClass } from '../../utils/deletedRow';
import { useToast } from '../../contexts/ToastContext';
import SamsaraLocationMapping from './SamsaraLocationMapping';

/** Read-only Yes/No pill for the table's boolean (Y/N) columns. */
const YesNo: React.FC<{ value?: string }> = ({ value }) => {
  const yes = value === 'Y';
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
        yes
          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
      }`}
    >
      {yes ? 'Yes' : 'No'}
    </span>
  );
};

/** Read-only numeric display (null → dash). */
const NumOrDash: React.FC<{ value?: number | null }> = ({ value }) =>
  value != null ? <span>{value}</span> : <span className="text-gray-400 dark:text-gray-500">-</span>;

interface GeofenceRow {
  id: string | number;
  location_type?: string;
  lname: string;
  ladd?: string;
  lcity?: string;
  lstate?: string;
  lzip?: string;
  rcvhours1?: string;
  rcvhours2?: string;
  lunchhours1?: string;
  lunchhours2?: string;
  droplive?: string;
  communication?: string;
  type: string;
  location: string;
  coordinates?: string;
  radius?: number;
  status: string;
  alias?: string;
  created_at: string;
  updated_at: string;
  coordinates_last_updated?: string | null;
  geofenceradius?: number | null;
  lastuseddatetime?: string | null;
  railbilled?: string;
  max_lane_capacity?: number | null;
  max_deliveries?: number | null;
  samsara_loc_id?: string | null;
  dwell_time_minutes?: number | null;
  recovery_time?: number | null;
  allow_bobtails?: string;
  allow_preplan?: string;
  allow_preplan_returns?: string;
  require_pickup?: string;
  require_ppe?: string;
  deleted?: string;
  createdatetime?: string | null;
}

const LocationsTable: React.FC = () => {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  // Optional ?recnos=1,2,3 deep-link (e.g. from the LC dashboard's Missing GPS Coordinates
  // bucket) — restricts the table to just those location recnos.
  const recnosParam = searchParams.get('recnos');
  const recnoFilter = useMemo(() => {
    if (!recnosParam) return null;
    const set = new Set(recnosParam.split(',').map((x) => x.trim()).filter(Boolean));
    return set.size ? set : null;
  }, [recnosParam]);
  const [data, setData] = useState<GeofenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  // When off (default) only non-deleted locations load; on loads all (incl. deleted).
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Geofence map drawer (kept available directly from the table as well).
  const [selectedGeofence, setSelectedGeofence] = useState<GeofenceRow | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Full-record edit/create drawer.
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState<'create' | 'edit'>('edit');
  const [editingLocation, setEditingLocation] = useState<GeofenceRow | null>(null);

  const handleViewGeofence = (geofence: GeofenceRow) => {
    setSelectedGeofence(geofence);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedGeofence(null);
  };

  const handleEditLocation = (location: GeofenceRow) => {
    setEditMode('edit');
    setEditingLocation(location);
    setEditDrawerOpen(true);
  };

  const handleAddLocation = () => {
    setEditMode('create');
    setEditingLocation(null);
    setEditDrawerOpen(true);
  };

  const handleCloseEditDrawer = () => {
    setEditDrawerOpen(false);
    setEditingLocation(null);
  };

  // From the dupe popup: switch the open create drawer to edit the chosen record.
  const handleEditExisting = (loc: DupeLocation) => {
    setEditMode('edit');
    setEditingLocation(loc as unknown as GeofenceRow);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // deleted='N' → only active; omit the param → all (incl. deleted).
      const params = includeDeleted ? {} : { deleted: 'N' };
      const response = await api.get('/geofences/list', { params });
      setData(response.data.data || []);
    } catch (error) {
      console.error('Error loading geofence data:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [includeDeleted]);

  // Load on mount and whenever the include-deleted toggle changes.
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Optional ?edit=<recno> deep-link — the order screen's Consignee pencil
  // button (EntityEditLink) opens the location straight into its edit drawer.
  // Unlike the tablemaint tables, LocationEditDrawer reads the whole row rather
  // than refetching from a recno, so this has to wait for loadData and resolve
  // against `data`. Latched so closing the drawer doesn't reopen it.
  const editParamConsumed = useRef(false);
  //: A deep link that missed has already widened the list to include deleted rows, so
  //  the retry after the reload must not widen it again — that would loop on a recno
  //  that simply does not exist. Holds the `data` array it widened FROM, not just a
  //  flag: flipping the toggle re-renders before the wider fetch lands, so for one pass
  //  the effect sees includeDeleted true, loading false, and the OLD rows — and would
  //  give up on a location that is about to arrive. A new array reference is the signal
  //  that the wider list is actually here.
  const widenedFrom = useRef<GeofenceRow[] | null>(null);
  useEffect(() => {
    if (editParamConsumed.current || loading) return;
    const recno = Number(searchParams.get('edit')) || 0;
    if (!recno) return;
    const row = data.find((r) => Number(r.id) === recno);
    if (row) {
      editParamConsumed.current = true;
      handleEditLocation(row);
      return;
    }
    // The list loads with deleted='N', so a soft-deleted location genuinely isn't here.
    // Widen and let the effect run again rather than telling the user to go and find the
    // toggle: legs point at retired locations on purpose (~200 open ones do), so a link
    // to one is a normal thing to follow, not a mistake to report. Retry once.
    if (!includeDeleted && !widenedFrom.current) {
      widenedFrom.current = data;
      setIncludeDeleted(true);
      return;
    }
    if (widenedFrom.current && data === widenedFrom.current) return;  // wider list pending
    editParamConsumed.current = true;
    showToast({
      message: `Location #${recno} could not be opened — no location with that record number.`,
      type: 'error',
    });
  }, [loading, data, searchParams, includeDeleted]);

  const handleSaveGeofence = () => {
    // Reload data after save
    loadData();
  };

  // Define columns for TanTable
  const columns = useMemo<ColumnDef<GeofenceRow>[]>(() => [
    {
      accessorKey: 'id',
      header: 'ID',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      size: 80,
      cell: (info) => <span className="text-muted">{info.getValue() as React.ReactNode}</span>,
    },
    {
      accessorKey: 'lname',
      header: 'Name',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: 'location_type',
      header: 'Location Type',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'railbilled',
      header: 'Rail Billed',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => (
        <div className="flex justify-center">
          <YesNo value={info.row.original.railbilled} />
        </div>
      ),
    },
    {
      accessorKey: 'ladd',
      header: 'Address',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'lcity',
      header: 'City',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'lstate',
      header: 'State',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'lzip',
      header: 'Zip',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'rcvhours1',
      header: 'Receive Hours 1',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'rcvhours2',
      header: 'Receive Hours 2',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'lunchhours1',
      header: 'Lunch Start',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'lunchhours2',
      header: 'Lunch End',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'droplive',
      header: 'Drop Live',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'communication',
      header: 'Communication',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'radius',
      header: 'Geofence Radius',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => {
        const row = info.row.original;
        const radiusValue = row.radius ?? row.geofenceradius;
        if (!radiusValue || radiusValue === 0) return <span className="text-gray-400 dark:text-gray-500">-</span>;
        return <span>{Number(radiusValue).toFixed(2)}</span>;
      },
    },
    {
      accessorKey: 'lastuseddatetime',
      header: 'Last Used',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => {
        const value = info.getValue() as string | null;
        if (!value) return <span className="text-gray-400 dark:text-gray-500">-</span>;
        const date = new Date(value);
        return (
          <span>
            {date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}{' '}
            {date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
          </span>
        );
      },
    },
    {
      accessorKey: 'coordinates_last_updated',
      header: 'Geofence Updated',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => {
        const value = info.getValue() as string | null;
        if (!value) return <span className="text-gray-400 dark:text-gray-500">-</span>;
        const date = new Date(value);
        return (
          <span>
            {date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}{' '}
            {date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
          </span>
        );
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      enableHiding: false,
      cell: (info) => info.getValue(),
    },
    {
      id: 'max_lane_capacity',
      accessorFn: (row: GeofenceRow) => row.max_lane_capacity != null ? String(row.max_lane_capacity) : 'None',
      header: 'Max Lane Capacity',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <NumOrDash value={info.row.original.max_lane_capacity} />,
    },
    {
      id: 'max_deliveries',
      accessorFn: (row: GeofenceRow) => row.max_deliveries != null ? String(row.max_deliveries) : 'None',
      header: 'Max Deliveries',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <NumOrDash value={info.row.original.max_deliveries} />,
    },
    {
      accessorKey: 'samsara_loc_id',
      header: 'Samsara Location ID',
      cell: (info) => info.getValue() || '—',
    },
    {
      id: 'dwell_time_minutes',
      accessorFn: (row: GeofenceRow) => row.dwell_time_minutes != null ? String(row.dwell_time_minutes) : 'None',
      header: 'Dwell Time (min)',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <NumOrDash value={info.row.original.dwell_time_minutes} />,
    },
    {
      id: 'recovery_time',
      accessorFn: (row: GeofenceRow) => row.recovery_time != null ? String(row.recovery_time) : 'None',
      header: 'Recovery Time (min)',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <NumOrDash value={info.row.original.recovery_time} />,
    },
    {
      accessorKey: 'allow_bobtails',
      header: 'Allow Bobtails',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <div className="flex justify-center"><YesNo value={info.row.original.allow_bobtails} /></div>,
    },
    {
      accessorKey: 'allow_preplan',
      header: 'Allow Preplan',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <div className="flex justify-center"><YesNo value={info.row.original.allow_preplan} /></div>,
    },
    {
      accessorKey: 'allow_preplan_returns',
      header: 'Allow Preplan Returns',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <div className="flex justify-center"><YesNo value={info.row.original.allow_preplan_returns} /></div>,
    },
    {
      accessorKey: 'require_pickup',
      header: 'Require Pickup #',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <div className="flex justify-center"><YesNo value={info.row.original.require_pickup} /></div>,
    },
    {
      accessorKey: 'require_ppe',
      header: 'Require PPE',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <div className="flex justify-center"><YesNo value={info.row.original.require_ppe} /></div>,
    },
    {
      accessorKey: 'deleted',
      header: 'Deleted',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <div className="flex justify-center"><YesNo value={info.row.original.deleted} /></div>,
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      // Row click opens the editor; the geofence map stays here (stopPropagation
      // so it doesn't also open the edit drawer).
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex justify-end gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); handleViewGeofence(row); }}
              className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 transition-colors"
              title="View/Edit Geofence on Map"
            >
              <MapIcon className="w-5 h-5" />
            </button>
          </div>
        );
      },
    },
  ], []);

  // When deep-linked with ?recnos=, show only those locations.
  const displayData = useMemo(
    () => (recnoFilter ? data.filter((r) => recnoFilter.has(String(r.id))) : data),
    [data, recnoFilter]
  );

  return (
    <div className="p-6">
      <div className="flex justify-end mb-3">
        <button className="px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-body hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => setSearchParams({ view: 'samsara-mapping' })}>Samsara bulk mapping</button>
      </div>
      {recnoFilter && (
        <div className="mb-3 flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
          <span>
            Showing {displayData.length} location{displayData.length === 1 ? '' : 's'} filtered by link
            {' '}(of {data.length} total).
          </span>
          <a href="/data-management/locations" className="font-medium underline hover:no-underline">
            Show all
          </a>
        </div>
      )}
      <TanTable
        data={displayData}
        columns={columns}
        loading={loading}
        cardTitle="Locations"
        settingsKey="locations"
        fieldName="locations-table"
        enableColumnFilters={true}
        enableExcelExport={true}
        enableSearch={true}
        enableReset={true}
        enableShowHideCols={true}
        enableRowsPerPage={false}
        rowsPerPageOptions={[100]}
        enableZebraStriping={true}
        defaultGlobalFilter={initialSearch}
        showDeletedToggle={true}
        showDeleted={includeDeleted}
        onShowDeletedChange={setIncludeDeleted}
        getRowClassName={(row, m) => deletedRowClass(row.deleted === 'Y', m.zebra)}
        onEditFilters={handleAddLocation}
        showEditFiltersButton={true}
        editFiltersButtonIcon="plus"
        editFiltersButtonTooltip="Add Location"
        onRowClick={handleEditLocation}
        lockedColumns={[
          { id: 'actions', side: 'right' }
        ]}
      />

      {selectedGeofence && (
        <GeofenceDrawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          geofence={selectedGeofence}
          onSave={handleSaveGeofence}
        />
      )}

      <LocationEditDrawer
        isOpen={editDrawerOpen}
        mode={editMode}
        location={editingLocation}
        onClose={handleCloseEditDrawer}
        onSaved={loadData}
        onEditExisting={handleEditExisting}
      />
    </div>
  );
};

const LocationsEditor: React.FC = () => {
  const [params, setParams] = useSearchParams();
  return params.get('view') === 'samsara-mapping'
    ? <SamsaraLocationMapping onBack={() => setParams({})} />
    : <LocationsTable />;
};

export default LocationsEditor;
